import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { printPayslip } from "@/lib/print";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollText, Loader2, Download } from "lucide-react";

export default function MyPayroll() {
  const { data: summary, isLoading, error } = useQuery({ queryKey: ["my-payroll"], queryFn: async () => (await api.get("/staff/me/summary")).data, retry: false });
  const { data: settings } = useQuery({ queryKey: ["all-settings"], queryFn: async () => (await api.get("/settings")).data });

  if (isLoading) return <div className="p-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (error) return <div className="max-w-md mx-auto mt-16 text-center text-slate-500" data-testid="my-payroll-empty"><ScrollText className="h-10 w-10 mx-auto mb-3 opacity-40" />No staff record is linked to your account.<br />Ask your admin to add you with your registered mobile number.</div>;

  const s = summary.staff;
  return (
    <div className="space-y-5 max-w-3xl" data-testid="my-payroll-page">
      <div className="flex items-center justify-between">
        <div><h2 className="font-display text-2xl font-bold text-[#0F172A] flex items-center gap-2"><ScrollText className="h-6 w-6 text-[#1A2A5E]" /> My Payroll</h2>
          <p className="text-slate-500 text-sm">{s.name} · {s.designation || "Staff"} · {summary.month} (read-only)</p></div>
        <Button variant="outline" onClick={() => printPayslip(summary, settings?.business, settings?.bill)} data-testid="my-payslip-pdf"><Download className="h-4 w-4 mr-1.5" />Payslip PDF</Button>
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        {[["Base", summary.base], ["Additions", summary.additions], ["Deductions", summary.deductions], ["Net Payable", summary.net]].map(([l, v]) => (
          <Card key={l} className="p-4 border-slate-200"><div className="text-xs text-slate-400">{l}</div><div className="text-lg font-mono font-bold text-[#1A2A5E]">{money(v)}</div></Card>
        ))}
      </div>

      <Card className="p-4 border-slate-200">
        <div className="text-sm font-semibold text-slate-700 mb-2">Salary Payments</div>
        <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead>Ref</TableHead></TableRow></TableHeader>
          <TableBody>{(summary.payments || []).length ? summary.payments.map((p, i) => (
            <TableRow key={i}><TableCell className="text-sm">{new Date(p.date).toLocaleDateString("en-IN")}</TableCell><TableCell className="text-right font-mono">{money(p.amount)}</TableCell>
              <TableCell><Badge variant="secondary" className="capitalize">{p.status}</Badge></TableCell><TableCell className="text-xs text-slate-400">{p.txn_ref || "—"}</TableCell></TableRow>
          )) : <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No payments yet</TableCell></TableRow>}</TableBody>
        </Table>
      </Card>

      <Card className="p-4 border-slate-200">
        <div className="text-sm font-semibold text-slate-700 mb-2">Attendance ({summary.month})</div>
        <div className="flex flex-wrap gap-1.5">
          {(summary.attendance || []).length ? summary.attendance.map((a, i) => (
            <span key={i} className={`text-xs px-2 py-1 rounded ${a.status === "present" ? "bg-emerald-100 text-emerald-700" : a.status === "absent" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{a.date.slice(8)} · {a.status[0].toUpperCase()}</span>
          )) : <span className="text-slate-400 text-sm">No attendance recorded</span>}
        </div>
      </Card>
    </div>
  );
}
